<template>
  <v-dialog v-model="editDialog" class="the-app-dialogs" persistent max-width="800px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t(`Edit Stockholder's Information`) }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout row wrap justify-end>
            <v-flex shrink>
              <v-chip v-if="fileSelected" :selected="fileSelected" @input="removeFile()" close dark color="primary">
                <v-avatar>
                  <v-icon>insert_drive_file</v-icon>
                </v-avatar>
                <a
                  v-if="attachment !== null"
                  :href="`${baseUrls.images}/${attachment}`"
                  target="_blank"
                  style="color: white; text-decoration: none"
                >
                  {{ $t('Click to View Attachment') }}
                </a>
                <span v-else>{{ fileName | truncate }}</span>
              </v-chip>
              <v-btn :disabled="fileSelected" @click="chooseFile" color="primary" class="white--text">
                <v-icon left dark>attach_file</v-icon>
                {{ $t('Attach file') }}
              </v-btn>
              <input
                type="file"
                name="file"
                ref="fileInput"
                style="visibility:hidden; width:0; position:absolute; top:-9999px"
                @input="onSelectFile"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap justify-content-end>
            <v-flex xs12 pa-2>
              <v-text-field
                v-model="viewInfo.stockholder_name"
                :label="`${$t('Stockholder Name')} *`"
                name="stockholder_name"
                box
                hide-details
                background-color="white"
                :error="errors.has('stockholder_name')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="viewInfo.number_of_shares"
                :label="`${$t('Number Of Shares')} *`"
                name="number_of_shares"
                box
                hide-details
                background-color="white"
                :error="errors.has('number_of_shares')"
                v-validate="'required|numeric'"
              />
            </v-flex>

            <v-flex xs6 pa-2 v-if="settings.opening_balance_status === 'Active'">
              <v-autocomplete
                v-model="viewInfo.cash_or_opening_balance"
                :label="`${$t('Cash / Opening Balance')} *`"
                hide-details
                :items="cashList"
                box
                data-vv-name="cash_or_opening_balance"
                background-color="white"
                :error="errors.has('cash_or_opening_balance')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="viewInfo.amount"
                :label="`${$t('Amount')} (${systemCurrency}) *`"
                name="amount"
                box
                hide-details
                background-color="white"
                autocomplete="off"
                :error="errors.has('amount')"
                v-validate="'required|decimal|min_value:1'"
              />
            </v-flex>

            <v-flex xs6 class="pa-2">
              <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
                <v-text-field
                  slot="activator"
                  :value="viewInfo.date"
                  :label="`${$t('Date')} *`"
                  prepend-inner-icon="event"
                  box
                  readonly
                  hide-details
                  name="date"
                  background-color="white"
                  autocomplete="off"
                  :error="errors.has('date')"
                  v-validate="'required|date_format:yyyy-MM-dd'"
                />
                <v-date-picker @change="dateMenu = false" v-model="viewInfo.date" />
              </v-menu>
            </v-flex>

            <v-flex xs12 class="pa-2">
              <v-textarea
                v-model="viewInfo.note"
                :label="$t('Note')"
                name="note"
                box
                hide-details
                height="80"
                background-color="white"
              />
            </v-flex>
          </v-layout>
        </v-card-title>

        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end>
              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnUpdateLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Update') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'edit-component',

  props: {
    editDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      btnUpdateLoading: false,
      dateMenu: false,
      attachment: null,
      fileSelected: false,
      fileName: '',
      fileData: null,
      fileInput: null,
      theFile: null
    }
  },

  computed: {
    ...mapGetters({
      viewInfo: 'stockholders/getStockholderInfo',
      baseUrls: 'misc/getBaseUrls',
      cashList: 'accountStatements/getCashOrOpenList',
      settings: 'configurations/getSystemSettings'
    })
  },

  watch: {
    editDialog: {
      immediate: false,
      handler() {
        if (this.editDialog) {
          this.removeFile()
          this.addEscKeyListener()

          if (this.viewInfo.attachment) {
            this.attachment = this.viewInfo.attachment
            this.fileSelected = true
          }
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updateStockholderInfo: 'stockholders/updateStockholderInfo'
    }),

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.editDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    chooseFile() {
      this.$refs.fileInput.click()
    },

    onSelectFile() {
      this.fileInput = this.$refs.fileInput
      this.theFile = this.fileInput.files

      if (this.theFile && this.theFile[0]) {
        const reader = new FileReader()
        reader.onload = e => {
          this.fileData = e.target.result
        }
        reader.readAsDataURL(this.theFile[0])
        this.$emit('input', this.theFile[0])
        this.fileName = this.theFile[0].name
        this.fileSelected = true
      }
    },

    removeFile() {
      this.fileData = null
      this.fileInput = null
      this.theFile = null
      this.$refs.fileInput.value = null
      this.attachment = null
      this.fileName = ''
      this.fileSelected = false
    },

    validateBeforeSubmit(event) {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any()) {
          this.btnUpdateLoading = true

          let formData = new FormData(event.target)
          formData.append('cash_or_opening_balance', this.viewInfo.cash_or_opening_balance)
          if (this.theFile === null && this.attachment === null) {
            formData.append('attachment', '')
          } else if (this.theFile !== null) {
            formData.append('attachment', this.theFile[0])
          }

          // for (let pair of formData.entries()) {
          //   console.warn(`${pair[0]}: ${pair[1]}`)
          // }

          let payload = { id: this.viewInfo.stockholder_id, formData }

          this.updateStockholderInfo(payload)
            .then(() => {
              this.btnUpdateLoading = false
              this.$emit('updated')
            })
            .catch(() => {
              this.btnUpdateLoading = false
            })
        }
      })
    }
  }
}
</script>

<style scoped />
